import { type ApprovalPoolConstructorData } from '@webapp/core/entities/internal/ApprovalPool'
import { type ApprovalWorkflowConstructorData } from '@webapp/core/entities/internal/ApprovalWorkflow'
import { type ApproverConstructor } from '@webapp/core/entities/internal/Approver'
import { type OrderSummaryConstructor } from '@webapp/modules/purchasing/entities/OrderSummary'
import {
  type OrderSummaryApproverApiModel,
  type OrderSummaryApproverGroupApiModel,
  type OrderSummaryApiModel,
  type OrderSummaryCurrentStepApiModel,
} from '../api-models/OrderSummaryApiModel'

type ApproverChoiceConstructorData =
  | ApproverConstructor
  | ApprovalPoolConstructorData

const mapOrderSummaryApprover = (
  approver: OrderSummaryApproverApiModel
): ApproverConstructor => ({
  id: approver.id,
  firstName: approver.firstName,
  lastName: approver.lastName,
  profileImage: approver.profile_image ?? null,
  approvalDelegatee: approver.approval_delegatee
    ? {
        id: approver.approval_delegatee.id,
        firstName: approver.approval_delegatee.firstName,
        lastName: approver.approval_delegatee.lastName,
        expectedReturnDate:
          approver.approval_delegatee.expected_return_date ?? null,
      }
    : null,
  primaryCategory: null,
  secondaryCategory: null,
})

// Collapses a wire {group_id, group_name, is_pooled, level, approvers} group
// into at most ONE choice, keyed off the group's approver count rather than
// `is_pooled` — the same rule NextApproverSelectBridge and mapOrderApiModel
// apply, so a lone approver stays a single auto-selectable choice and
// approving a multi-approver group routes to every member.
const mapApproverGroup = (
  group: OrderSummaryApproverGroupApiModel
): ApproverChoiceConstructorData[] => {
  if (group.approvers.length === 0) {
    return []
  }

  if (group.approvers.length > 1) {
    const pool: ApprovalPoolConstructorData = {
      level: group.level,
      groupName: group.group_name,
      groupId: group.group_id,
      members: group.approvers.map(mapOrderSummaryApprover),
    }
    return [pool]
  }

  return [
    {
      ...mapOrderSummaryApprover(group.approvers[0]),
      level: group.level,
      groupName: group.group_name,
    },
  ]
}

const mapCurrentStep = (
  currentStep: OrderSummaryCurrentStepApiModel
): ApprovalWorkflowConstructorData['currentStep'] => {
  const [approver] = mapApproverGroup(currentStep.approver)

  if (!approver) {
    return null
  }

  return {
    groupId: currentStep.group_id,
    groupName: currentStep.group_name,
    approver,
  }
}

const mapApprovalWorkflow = (
  approvalWorkflow: NonNullable<OrderSummaryApiModel['approval_workflow']>
): ApprovalWorkflowConstructorData => ({
  currentStep: approvalWorkflow.current_step
    ? mapCurrentStep(approvalWorkflow.current_step)
    : null,
  nextStep: approvalWorkflow.next_step
    ? {
        groupId: approvalWorkflow.next_step.group_id,
        groupName: approvalWorkflow.next_step.group_name,
        approverChoices: approvalWorkflow.next_step.options.reduce<
          ApproverChoiceConstructorData[]
        >((acc, option) => acc.concat(mapApproverGroup(option)), []),
      }
    : null,
})

export const mapOrderSummaryApiModel = (
  raw: OrderSummaryApiModel
): OrderSummaryConstructor => {
  // Mandatory delegation-state correction (StatusListManagerFormat.js:76-80
  // parity) for the LEGACY field only: a null row-level approval_delegatee
  // means there is no live delegation, so the stale nested
  // next_approver.approval_delegatee must be nulled out. Workflow approvers
  // are serialized from the live approver and need no correction.
  const mappedNextApprover = raw.next_approver
    ? mapOrderSummaryApprover(raw.next_approver)
    : null

  const nextApprover: Nullable<ApproverConstructor> = mappedNextApprover
    ? {
        ...mappedNextApprover,
        approvalDelegatee: raw.approval_delegatee
          ? mappedNextApprover.approvalDelegatee
          : null,
      }
    : null

  const punchoutShippingAddressName: Nullable<string> =
    typeof raw.punchout_shipping_address === 'string'
      ? raw.punchout_shipping_address
      : (raw.punchout_shipping_address?.name ?? null)

  return {
    orderNumber: raw.order_number ?? raw.num,
    id: raw.num,
    uuid: raw.uuid,
    status: raw.status,
    description: raw.description,
    totalCostInBaseCurrency: raw.total_cost_in_base_currency,
    itemCount: raw.item_count,
    submittedDate: raw.date,
    dateRequired: raw.dateRequired,
    primaryCategoryName: raw.department.branch.name,
    secondaryCategoryName: raw.department.name,
    nextApprover,
    nextApproverChoices: raw.next_approver_choices
      ? raw.next_approver_choices.map(mapOrderSummaryApprover)
      : null,
    approvalWorkflow: raw.approval_workflow
      ? mapApprovalWorkflow(raw.approval_workflow)
      : null,
    hasPendingItems: raw.has_pending_items,
    hasPendingPunchoutItems: raw.has_pending_punchout_items,
    hasBlanketOrderItems: raw.has_blanket_order_items,
    delegatedBy: raw.delegated_by,
    isPunchoutOrder: raw.punchout_group !== null,
    punchoutItemsRejected: raw.punchout_items_rejected,
    punchoutSupplierName: raw.punchout_supplier_name,
    punchoutSupplierIcon: raw.punchout_supplier_icon,
    punchoutShippingAddressName,
  }
}
